import React, { useState, useCallback } from 'react';
import VehicleImage from 'components/VehicleImage';
import { FormattedPlural } from 'react-intl';
import { Button } from '@ontruck/orc';
import { Loading } from 'components/Loading';

import { createAndOfferPrebooking } from 'modules/prebookings/services';
import { prepareForCreation } from 'modules/prebookings/utils';

import { FormatPrice } from 'utils/format';

const t = {
  slot: 'slot',
  slots: 'slots',
  launch: 'Launch',
  suggested_forecast: 'Suggestion',
  suggested_forecast_tooltip: 'Numbers of prebooking suggested to prebook',
  num_sos_to_forecast: 'SOs',
  num_sos_to_forecast_tooltip: 'Shipper Orders used to calculate the forecast',
  num_vehicles_needed_to_forecast: 'Vehicles',
  num_vehicles_needed_to_forecast_tooltip: 'Vehicles need based on forecast',
  launched: 'Launched',
  accepted: 'Accepted',
  launched_success: 'Launched!',
  distance: 'Km',
  currency: 'EUR',
};

const formattedSlots = (slotsNumber) => (
  <FormattedPlural value={slotsNumber} one={t.slot} other={t.slots} />
);

const vehicleTypeIconModifiers = {
  tractor_unit: 'trailer',
};

const PrebookingRow = ({ className, prebooking, onLaunch }) => {
  const { vehicle_type, amount, distance, acceptance_max, prebookingStatus, vehicles_need } =
    prebooking;

  const [loading, setLoading] = useState(false);
  const [slotsValue, setSlotsValue] = useState(acceptance_max);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSlotsValue(value);
  }, []);

  const handleLaunch = useCallback(async () => {
    try {
      setLoading(true);
      await createAndOfferPrebooking(prepareForCreation(prebooking, slotsValue));
      onLaunch && onLaunch();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [prebooking, slotsValue, setLoading, onLaunch]);

  const { launched, accepted } = prebookingStatus || {};

  const isAlreadyLaunched = launched > 0;

  const vehicleType = vehicleTypeIconModifiers[vehicle_type] || vehicle_type;

  return (
    <tr className={className}>
      <td width="50" className="cell cell--center cell--dark">
        <VehicleImage type={vehicleType} size="2.5rem" color="grey500" />
      </td>
      <td width="50" className="cell cell--right cell--dark">
        {<FormatPrice value={amount} currency={t.currency} />}
      </td>
      <td width="70" className="cell cell--right cell--dark">
        {distance} {t.distance}
      </td>
      <td className="cell cell--nopadding">
        <div className="status">
          <div
            className="status-box"
            data-tip={t.num_vehicles_needed_to_forecast_tooltip}
            data-for="prebookingsListTooltip"
          >
            <div className="hint hint--light">{t.num_vehicles_needed_to_forecast}</div>
            <strong>{vehicles_need === 0 || vehicles_need ? vehicles_need : '-'}</strong>
            <span></span>
          </div>
          <div
            className="status-box"
            data-tip={t.suggested_forecast_tooltip}
            data-for="prebookingsListTooltip"
          >
            <div className="hint hint--light">{t.suggested_forecast}</div>
            <strong>
              {acceptance_max} {formattedSlots(acceptance_max)}
            </strong>
          </div>

          <div className="status-box">
            <div className="hint hint--light">{t.launched}</div>
            <strong>{launched || '-'}</strong>
          </div>
          <div className="status-box">
            <div className="hint hint--light">{t.accepted}</div>
            <strong>{accepted === 0 || accepted ? accepted : '-'}</strong>
          </div>
        </div>
      </td>
      <td className="cell cell--right" width="110">
        <div className="launch-cell launch-cell--right">
          {loading && <Loading size={20} className="loading" />}
          <input
            type="text"
            value={isAlreadyLaunched ? launched : slotsValue}
            onChange={handleInputChange}
            disabled={loading || isAlreadyLaunched}
          />
        </div>
      </td>
      <td width="110" className="cell cell--left">
        <div className="launch-cell launch-cell--left">
          {isAlreadyLaunched ? (
            <div className="launch-cell--launched">{t.launched_success}</div>
          ) : (
            <Button nude className="button" onClick={handleLaunch} disabled={loading}>
              {t.launch} {slotsValue} {formattedSlots(slotsValue)}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default PrebookingRow;
